import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Modal from "react-modal";
import ReactLoading from "react-loading";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt, faTimesCircle, faArrowRight, faBriefcase, faCogs, faLightbulb, faGraduationCap, faCertificate, faTrophy } from "@fortawesome/free-solid-svg-icons";
import resumeData from "../data/interactiveResume.json";
import { blogAPI } from "../utils/API";
import * as DataEngine from "../utils/DataEngine";
import Content from "./Content";
import "../styles/Resume.css";
import amazonLogo from "../images/logos/aws.png";
import raytheonLogo from "../images/logos/rtx.png";
import linkedinLogo from "../images/logos/linkedin.png";
import geLogo from "../images/logos/ge.png";
import cornellLogo from "../images/logos/cornell.png";
import localBlogPosts from "../data/localBlogPosts.json";
import * as shuffle from "shuffle-array";

const LOW_SKILL_COLOR = "#2596be";
const HIGH_SKILL_COLOR = "#49be25";

function hexToRgb(hex) {
  let normalized = hex.replace('#', '');
  if (normalized.length === 3) {
    normalized = normalized
      .split('')
      .map((ch) => ch + ch)
      .join('');
  }
  const intVal = parseInt(normalized, 16);
  return {
    r: (intVal >> 16) & 255,
    g: (intVal >> 8) & 255,
    b: intVal & 255
  };
}

function rgbToHex({ r, g, b }) {
  const toHex = (value) => value.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function mixColors(colorA, colorB, amount) {
  const clamp = (value) => Math.round(Math.min(255, Math.max(0, value)));
  return {
    r: clamp(colorA.r * (1 - amount) + colorB.r * amount),
    g: clamp(colorA.g * (1 - amount) + colorB.g * amount),
    b: clamp(colorA.b * (1 - amount) + colorB.b * amount)
  };
}

const LOW_SKILL_RGB = hexToRgb(LOW_SKILL_COLOR);
const HIGH_SKILL_RGB = hexToRgb(HIGH_SKILL_COLOR);
const WHITE_RGB = { r: 255, g: 255, b: 255 };
const BLACK_RGB = { r: 0, g: 0, b: 0 };

function buildSkillGradient(value) {
  const intensity = Math.min(1, Math.max(0, value));
  const baseColor = mixColors(LOW_SKILL_RGB, HIGH_SKILL_RGB, intensity);
  const highlightColor = mixColors(baseColor, WHITE_RGB, 0.25);
  const darkerEdge = mixColors(baseColor, BLACK_RGB, 0.15);
  return `linear-gradient(90deg, ${rgbToHex(darkerEdge)} 0%, ${rgbToHex(baseColor)} 55%, ${rgbToHex(highlightColor)} 100%)`;
}

const NAV_ITEMS = [
  { id: "experience", label: "Experience" },
  { id: "skills", label: "Skills" },
  { id: "education", label: "Education" },
  { id: "certifications", label: "Certifications" },
  { id: "recognition", label: "Awards" },
  { id: "blog", label: "Insights" },
  {
    label: "LinkedIn",
    external: true,
    url: "https://www.linkedin.com/in/bionicly"
  }
];

const Home = () => {
  const [resumeDetail, setResumeDetail] = useState(null);
  const [posts, setPosts] = useState([]);
  const [layout, setLayout] = useState([]);
  const [columns, setColumns] = useState(1);
  const [gridWidth, setGridWidth] = useState(0);
  const [showPostModal, setShowPostModal] = useState(false);
  const [activePost, setActivePost] = useState(null);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const resumeRef = useRef(null);
  const resumePdfPath = useMemo(() => {
    const base = process.env.PUBLIC_URL || "";
    return `${base}/bionicly_resume_11182025.pdf`;
  }, []);

  useEffect(() => {
    Modal.setAppElement("#root");
  }, []);

  const handleNavClick = useCallback((targetId) => {
    const el = document.getElementById(targetId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // Close mobile nav after clicking
    setNavOpen(false);
  }, []);

  const toggleNav = useCallback(() => {
    setNavOpen(prev => !prev);
  }, []);

  const calculateLayouts = useCallback((data) => {
    if (!data.length) {
      return { layout: [], columns: 1, width: window.innerWidth };
    }

    const checkSmartPhone = window.matchMedia("(max-width: 768px)");
    const checkDesktop = window.matchMedia("(min-width: 1200px)");

    if (checkSmartPhone.matches) {
      const positions = data.map((post, idx) => ({
        i: post.id,
        x: 0,
        y: idx,
        w: 1,
        h: 1,
        static: true
      }));
      return {
        layout: positions,
        columns: 1,
        width: window.innerWidth
      };
    }

    if (checkDesktop.matches) {
      const positions = [];
      let row = 0;
      data.forEach((post, idx) => {
        positions.push({
          i: post.id,
          x: idx % 3,
          y: row,
          w: 1,
          h: 1,
          static: true,
          title: post.title
        });
        if (idx % 3 === 2) {
          row += 1;
        }
      });

      return {
        layout: shuffle(positions, { copy: true }),
        columns: 3,
        width: window.innerWidth * 0.75
      };
    }

    const positions = [];
    let row = 0;
    data.forEach((post, idx) => {
      positions.push({
        i: post.id,
        x: idx % 2,
        y: row,
        w: 1,
        h: 1,
        static: true
      });
      if (idx % 2 === 1) {
        row += 1;
      }
    });

    return {
      layout: positions,
      columns: 2,
      width: window.innerWidth * 0.9
    };
  }, []);

  const applyLayout = useCallback(
    (data) => {
      const { layout: newLayout, columns: newColumns, width } = calculateLayouts(data);
      setLayout(newLayout);
      setColumns(newColumns);
      setGridWidth(width);
    },
    [calculateLayouts]
  );

  useEffect(() => {
    const fetchPosts = async () => {
      setLoadingPosts(true);
      let merged = [];
      try {
        const response = await blogAPI.get("");
        // WordPress API returns posts directly in response.data.posts
        const postsData = response.data.posts || response.data;
        const processedBlog = DataEngine.processBlogs(postsData);
        merged = DataEngine.mergeData(processedBlog, []);
      } catch (err) {
        console.warn("Falling back to local blog data due to fetch error", err);
        try {
          const fallbackRaw = localBlogPosts.posts || localBlogPosts;
          const processedFallback = DataEngine.processBlogs(fallbackRaw);
          merged = DataEngine.mergeData(processedFallback, []);
        } catch (fallbackErr) {
          console.error("Failed to load fallback blog posts", fallbackErr);
        }
      } finally {
        if (merged.length) {
          setPosts(merged);
          applyLayout(merged);
        }
        setLoadingPosts(false);
      }
    };

    fetchPosts();
  }, [applyLayout]);

  useEffect(() => {
    if (!posts.length) {
      return;
    }
    const handleResize = () => applyLayout(posts);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [posts, applyLayout]);

  const openDetail = useCallback((detail) => {
    setResumeDetail(detail);
  }, []);

  const closeDetail = useCallback(() => {
    setResumeDetail(null);
  }, []);

  const getPost = useCallback((id) => {
    const post = posts.find((item) => item.id === id);
    if (post) {
      setActivePost(post);
      setShowPostModal(true);
    }
  }, [posts]);

  const closePost = useCallback(() => {
    setShowPostModal(false);
    setActivePost(null);
  }, []);

  const processBody = useCallback((post) => {
    if (post.type === "tumblr" || post.type === "apps") {
      return <div className="body" dangerouslySetInnerHTML={{ __html: post.body }} />;
    }
    return <div className="body">{post.body}</div>;
  }, []);

  const resumeCards = useMemo(
    () =>
      resumeData.experience.map((exp) => ({
        title: exp.role,
        subtitle: `${exp.company} · ${exp.period}`,
        highlights: exp.highlights,
        body: exp.details,
        links: exp.links || []
      })),
    []
  );

  const contactActions = useMemo(
    () =>
      (resumeData.profile.contact || []).map((item) => ({
        ...item,
        key: `${item.label}-${item.value}`
      })),
    []
  );

  const educationCards = useMemo(
    () =>
      resumeData.education.map((edu) => ({
        title: edu.program,
        subtitle: `${edu.institution} · ${edu.year}`,
        highlights: edu.honors ? [edu.honors] : [],
        body: edu.details || [],
        links: edu.url
          ? [
              {
                label: "Program overview",
                url: edu.url
              }
            ]
          : []
      })),
    []
  );

  const certificationCards = useMemo(
    () =>
      resumeData.certifications.map((cert) => ({
        title: cert.name,
        subtitle: `${cert.issuer} · ${cert.year}`,
        body: cert.details || [],
        links: cert.url
          ? [
              {
                label: cert.issuer,
                url: cert.url
              }
            ]
          : []
      })),
    []
  );

  const skillCards = useMemo(
    () =>
      resumeData.skills.map((skill) => ({
        title: skill.category,
        subtitle: skill.level,
        body: skill.items || [],
        proficiency: skill.proficiencyScore
      })),
    []
  );

  const recognitionLogos = useMemo(
    () => ({
      amazon: amazonLogo,
      raytheon: raytheonLogo,
      linkedin: linkedinLogo,
      ge: geLogo,
      cornell: cornellLogo
    }),
    []
  );

  const recognitionCards = useMemo(
    () =>
      resumeData.recognition.map((item) => ({
        title: item.title,
        organization: item.organization,
        logo: recognitionLogos[item.logo]
      })),
    [recognitionLogos]
  );

  const handleCardClick = useCallback((card, sectionLabel) => {
    openDetail({
      title: card.title,
      subtitle: card.subtitle || sectionLabel,
      highlights: card.highlights || [],
      body: card.body || [],
      links: card.links || [],
      section: sectionLabel
    });
  }, [openDetail]);

  return (
    <div className="home-page">
      <div className="home-nav-wrapper">
        <div className="home-nav-brand">
          <span className="home-nav-name">{resumeData.profile.name}</span>
          <span className="home-nav-role">{resumeData.profile.title}</span>
        </div>
        <button 
          className={`home-nav-toggle ${navOpen ? 'active' : ''}`}
          onClick={toggleNav}
          aria-label="Toggle navigation menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        <nav className={`home-nav ${navOpen ? 'nav-open' : ''}`}>
          {NAV_ITEMS.map((item) =>
            item.external ? (
              <a
                key={item.label}
                href={item.url}
                className="home-nav-link home-nav-link--external"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setNavOpen(false)}
              >
                {item.label}
              </a>
            ) : (
              <button
                type="button"
                key={item.id}
                className="home-nav-link"
                onClick={() => handleNavClick(item.id)}
              >
                {item.label}
              </button>
            )
          )}
        </nav>
      </div>

      <div className="resume-page" ref={resumeRef}>
        <section className="resume-summary">
          <div className="resume-summary-list">
            {resumeData.profile.summary.map((line, idx) => {
              // Check if this is the last line that ends with 'relentless forward progress.'
              const isLastLine = idx === resumeData.profile.summary.length - 1;
              const endsWithProgress = line.includes("'relentless forward progress.'");
              
              if (isLastLine && endsWithProgress) {
                // Split the line to insert the PDF link before the closing quote
                const beforeProgress = line.substring(0, line.indexOf("'relentless forward progress.'"));
                const afterProgress = line.substring(line.indexOf("'relentless forward progress.'") + "'relentless forward progress.'".length);
                
                return (
                  <p key={idx}>
                    {beforeProgress}'relentless forward progress.' {" "}
                    <a
                      href={resumePdfPath}
                      className="resume-inline-pdf-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >
                      Download Resume PDF
                    </a>
                    {afterProgress}
                  </p>
                );
              }
              
              return <p key={idx}>{line}</p>;
            })}
          </div>
        </section>

        <main className="resume-content">
          <section id="experience" className="resume-section resume-section--experience home-section">
            <h3 className="resume-section-heading">
              <FontAwesomeIcon icon={faBriefcase} className="resume-section-icon" />
              Experience
            </h3>
            <div className="resume-card-grid">
              {resumeCards.map((card, idx) => (
                <button
                  type="button"
                  key={`experience-${idx}`}
                  className="resume-card"
                  onClick={() => handleCardClick(card, "Experience")}
                >
                  <div className="resume-card-header">
                    <h4>{card.title}</h4>
                    <span className="resume-card-subtitle">{card.subtitle}</span>
                  </div>
                  <ul className="resume-card-highlights">
                    {card.highlights.map((highlight, hIdx) => (
                      <li key={hIdx} dangerouslySetInnerHTML={{ __html: highlight }}></li>
                    ))}
                  </ul>
                  <div className="resume-card-cta">
                    Explore details <FontAwesomeIcon icon={faArrowRight} />
                  </div>
                </button>
              ))}
            </div>
          </section>

          <section id="skills" className="resume-section resume-section--skills home-section">
            <h3 className="resume-section-heading">
              <FontAwesomeIcon icon={faCogs} className="resume-section-icon" />
              Hands-on Skills
            </h3>
            <div className="resume-stack">
              {skillCards.map((card, idx) => {
                const normalized = Math.min(1, Math.max(0, card.proficiency ?? 0));
                const widthPercent = `${Math.round(normalized * 100)}%`;
                const gradientFill = buildSkillGradient(normalized);
                return (
                  <button
                    type="button"
                    key={`skill-${idx}`}
                    className="resume-mini-card resume-skill-card"
                    onClick={() => handleCardClick(card, "Skill focus")}
                  >
                    <h4>{card.title}</h4>
                    <span className="resume-card-subtitle">{card.subtitle}</span>
                    {card.body?.length ? (
                      <ul className="resume-card-highlights resume-card-highlights--skill">
                        {card.body.map((item, itemIdx) => (
                          <li key={itemIdx}>{item}</li>
                        ))}
                      </ul>
                    ) : null}
                      <div className="resume-skill-meter" aria-hidden="true">
                        <div className="resume-skill-meter-track">
                          <div
                            className="resume-skill-meter-fill"
                            style={{ width: widthPercent, background: gradientFill }}
                          />
                        </div>
                        <span className="resume-skill-meter-value">{card.subtitle}</span>
                      </div>
                    </button>
                );
              })}
            </div>
          </section>

          <div className="resume-column resume-column--details">
            <section id="education" className="resume-section home-section">
              <h3 className="resume-section-heading">
                <FontAwesomeIcon icon={faGraduationCap} className="resume-section-icon" />
                Education
              </h3>
              <div className="resume-stack">
                {educationCards.map((card, idx) => (
                  <button
                    type="button"
                    key={`education-${idx}`}
                    className="resume-mini-card"
                    onClick={() => handleCardClick(card, "Education")}
                  >
                    <h4>{card.title}</h4>
                    <span className="resume-card-subtitle">{card.subtitle}</span>
                    {card.highlights?.length ? (
                      <ul className="resume-card-highlights">
                        {card.highlights.map((highlight, hIdx) => (
                          <li key={hIdx}>{highlight}</li>
                        ))}
                      </ul>
                    ) : null}
                  </button>
                ))}
              </div>
            </section>

            <section id="certifications" className="resume-section home-section">
              <h3 className="resume-section-heading">
                <FontAwesomeIcon icon={faCertificate} className="resume-section-icon" />
                Certifications
              </h3>
              <div className="resume-stack">
                {certificationCards.map((card, idx) => (
                  <button
                    type="button"
                    key={`cert-${idx}`}
                    className="resume-mini-card"
                    onClick={() => handleCardClick(card, "Certification")}
                  >
                    <h4>{card.title}</h4>
                    <span className="resume-card-subtitle">{card.subtitle}</span>
                  </button>
                ))}
              </div>
            </section>

            <section id="recognition" className="resume-section resume-section--recognition resume-recognition-column home-section">
              <h3 className="resume-section-heading">
                <FontAwesomeIcon icon={faTrophy} className="resume-section-icon" />
                Recognition & Awards
              </h3>
              <div className="resume-recognition-grid">
                {recognitionCards.map((card, idx) => (
                  <button
                    type="button"
                    key={`recognition-${idx}`}
                    className="resume-recognition-item"
                    onClick={() => handleCardClick(card, "Recognition")}
                  >
                    {card.logo ? (
                      <img src={card.logo} alt={card.organization} className="resume-recognition-logo" />
                    ) : null}
                    <span className="resume-recognition-text">{card.title}</span>
                  </button>
                ))}
              </div>
            </section>
          </div>
          <section id="blog" className="home-section blog-section">
          <h3 className="resume-section-heading">
            <FontAwesomeIcon icon={faLightbulb} className="resume-section-icon" />
            Insights
          </h3>
          {loadingPosts ? (
            <div className="loadingScreen">
              <ReactLoading type="bars" color="#35a7ff" />
              Loading posts...
            </div>
          ) : (
            <Content
              gridWidth={gridWidth}
              layout={layout}
              columns={columns}
              padding={10}
              posts={posts}
              processBody={processBody}
              getPost={getPost}
            />
          )}
        </section>
        </main>

        
      </div>

      <Modal
        isOpen={Boolean(resumeDetail)}
        onRequestClose={closeDetail}
        className="resume-modal"
        overlayClassName="resume-modal-overlay"
        contentLabel="Resume detail"
      >
        {resumeDetail ? (
          <div className="resume-modal-content">
            <div className="resume-modal-header">
              <div>
                <h3>{resumeDetail.title}</h3>
                {resumeDetail.subtitle ? (
                  <p className="resume-modal-subtitle">{resumeDetail.subtitle}</p>
                ) : null}
              </div>
              <button
                type="button"
                className="resume-modal-close"
                onClick={closeDetail}
                aria-label="Close resume detail"
              >
                <FontAwesomeIcon icon={faTimesCircle} />
              </button>
            </div>

            {resumeDetail.highlights?.length ? (
              <ul className="resume-modal-highlights">
                {resumeDetail.highlights.map((item, idx) => (
                  <li key={idx} dangerouslySetInnerHTML={{ __html: item }}></li>
                ))}
              </ul>
            ) : null}

            {resumeDetail.body?.length ? (
              <div className="resume-modal-body">
                {resumeDetail.body.map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
            ) : null}

            {resumeDetail.links?.length ? (
              <div className="resume-modal-links">
                {resumeDetail.links.map((link, idx) => (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="resume-modal-link"
                  >
                    {link.label} <FontAwesomeIcon icon={faExternalLinkAlt} />
                  </a>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={showPostModal && Boolean(activePost)}
        onRequestClose={closePost}
        className="Modal"
        overlayClassName="Overlay"
        contentLabel="Blog post detail"
      >
        {activePost ? (
          <div className="modalBody blog-modal-body">
            <div className="modalMenu blog-modal-menu">
              <span className="modalTitle blog-modal-menuTitle">Insights</span>
              <span className="modalMenuOptions">
                <span className="modalIcon">
                  <FontAwesomeIcon icon={faTimesCircle} onClick={closePost} />
                </span>
                <span className="modalIcon">
                  <FontAwesomeIcon
                    icon={faExternalLinkAlt}
                    onClick={() => window.open(activePost.url, "_blank")}
                  />
                </span>
              </span>
            </div>
            <h3 className="blog-modal-bodyTitle">
              <a
                href="#"
                className="blog-modal-backLink"
                onClick={(e) => {
                  e.preventDefault();
                  closePost();
                }}
                aria-label="Back to insights"
              >
                ←
              </a>
              <span className="blog-modal-bodyTitleText">{activePost.title}</span>
            </h3>
            {activePost.featured_image && (
              <div className="blog-modal-featured-image">
                <img 
                  src={activePost.featured_image} 
                  alt={activePost.title}
                />
              </div>
            )}
            {processBody(activePost)}
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default Home;
